import { Component } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, map } from 'rxjs';
import {
  CONTACT_SUPPORT_EMAIL,
  buildContactMailtoUrl,
  buildContactSubject,
  buildGmailComposeUrl,
  buildOutlookComposeUrl,
} from '@core/utils/contact-support.util';
import { AccountStoreFacade } from 'src/app/store/account/account.facade';

/**
 * How a school reaches LOQT from the help.
 *
 * Three ways out, because a browser with no registered mail handler does nothing at all with a
 * `mailto:`, which read as a dead button: the mail app, Gmail's web compose and Outlook on the
 * web. The last two need nothing of the machine. The address and the subject are on screen and
 * copyable as well, so there is always a way to write to us.
 */
@Component({
  selector: 'app-contact-us-dialog',
  templateUrl: './contact-us-dialog.component.html',
  styleUrls: ['./contact-us-dialog.component.scss'],
  standalone: false,
})
export class ContactUsDialogComponent {
  readonly email = CONTACT_SUPPORT_EMAIL;
  readonly subject$: Observable<string>;
  readonly mailtoUrl$: Observable<string>;
  readonly gmailUrl$: Observable<string>;
  readonly outlookUrl$: Observable<string>;

  constructor(
    private dialogRef: MatDialogRef<ContactUsDialogComponent>,
    private snackBar: MatSnackBar,
    accountStore: AccountStoreFacade
  ) {
    this.subject$ = accountStore.schoolInfo$.pipe(map((school) => buildContactSubject(school?.name)));
    this.mailtoUrl$ = accountStore.schoolInfo$.pipe(map((school) => buildContactMailtoUrl(school?.name)));
    this.gmailUrl$ = accountStore.schoolInfo$.pipe(map((school) => buildGmailComposeUrl(school?.name)));
    this.outlookUrl$ = accountStore.schoolInfo$.pipe(map((school) => buildOutlookComposeUrl(school?.name)));
  }

  /** The way out for a browser that cannot open a mail app: take the address to one that can. */
  copyEmail(): void {
    const clipboard = navigator.clipboard;
    if (!clipboard) {
      this.snackBar.open(`Write to ${this.email}`, 'Close', { duration: 8000 });
      return;
    }

    clipboard.writeText(this.email).then(
      () => this.snackBar.open(`${this.email} copied`, 'Close', { duration: 4000 }),
      () => this.snackBar.open(`Write to ${this.email}`, 'Close', { duration: 8000 })
    );
  }

  close(): void {
    this.dialogRef.close();
  }
}
